import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/constants";
import "./LikeButton.css";

const GUEST_LIKES_KEY = "aminii_guest_likes";

export const getGuestLikes = () => {
  try {
    const raw = localStorage.getItem(GUEST_LIKES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveGuestLike = (productId, liked) => {
  try {
    const existing = getGuestLikes();
    let updated;
    if (liked) {
      updated = Array.from(new Set([...existing, productId]));
    } else {
      updated = existing.filter((id) => id !== productId);
    }
    localStorage.setItem(GUEST_LIKES_KEY, JSON.stringify(updated));
    window.dispatchEvent(
      new CustomEvent("aminii_like_updated", {
        detail: { productId, isLiked: liked },
      })
    );
  } catch (e) {
    // Ignore localStorage errors
  }
};

function LikeButton({
  productId,
  initialCount = 0,
  isLikedByUser = false,
  className = "",
  style = {},
}) {
  const [isLiked, setIsLiked] = useState(isLikedByUser);
  const [likeCount, setLikeCount] = useState(initialCount);
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toastTimeoutRef = useRef(null);

  // Sync initial props
  useEffect(() => {
    if (isLikedByUser !== undefined) {
      setIsLiked(Boolean(isLikedByUser));
    }
  }, [isLikedByUser]);

  useEffect(() => {
    if (initialCount !== undefined) {
      setLikeCount(Number(initialCount) || 0);
    }
  }, [initialCount]);

  // Check guest likes if user is not logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token && productId) {
      const guestLikes = getGuestLikes();
      if (guestLikes.includes(productId)) {
        setIsLiked(true);
      }
    }
  }, [productId]);

  // Listen to global like updates across cards/pages
  useEffect(() => {
    const handleLikeUpdate = (e) => {
      if (e.detail && e.detail.productId === productId) {
        setIsLiked(Boolean(e.detail.isLiked));
        if (typeof e.detail.newCount === "number") {
          setLikeCount(e.detail.newCount);
        }
      }
    };

    window.addEventListener("aminii_like_updated", handleLikeUpdate);
    return () => {
      window.removeEventListener("aminii_like_updated", handleLikeUpdate);
    };
  }, [productId]);

  const handleToggleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting || !productId) return;

    const token = localStorage.getItem("token");
    const previousLiked = isLiked;
    const previousCount = likeCount;

    const nextLiked = !previousLiked;
    const nextCount = Math.max(0, previousCount + (nextLiked ? 1 : -1));

    // Optimistic UI update
    setIsLiked(nextLiked);
    setLikeCount(nextCount);
    setIsSubmitting(true);

    try {
      if (token) {
        // Authenticated user toggle
        const res = await axios.post(
          `${API_BASE_URL}/likes/${productId}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        const serverData = res.data?.data || res.data;
        const serverLiked =
          typeof serverData?.liked === "boolean"
            ? serverData.liked
            : nextLiked;
        const serverCount =
          typeof serverData?.likeCount === "number"
            ? serverData.likeCount
            : nextCount;

        setIsLiked(serverLiked);
        setLikeCount(serverCount);

        window.dispatchEvent(
          new CustomEvent("aminii_like_updated", {
            detail: {
              productId,
              isLiked: serverLiked,
              newCount: serverCount,
            },
          })
        );
      } else {
        // Guest user like
        saveGuestLike(productId, nextLiked);

        const res = await axios.post(
          `${API_BASE_URL}/likes/guest/${productId}`,
          {},
          { withCredentials: true }
        );

        const serverData = res.data?.data || res.data;
        const serverCount =
          typeof serverData?.likeCount === "number"
            ? serverData.likeCount
            : nextCount;

        setLikeCount(serverCount);

        // Show a brief tip for guest users on liking
        if (nextLiked) {
          setShowToast(true);
          if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
          toastTimeoutRef.current = setTimeout(() => {
            setShowToast(false);
          }, 2400);
        }

        window.dispatchEvent(
          new CustomEvent("aminii_like_updated", {
            detail: {
              productId,
              isLiked: nextLiked,
              newCount: serverCount,
            },
          })
        );
      }
    } catch (err) {
      // Revert on failure
      setIsLiked(previousLiked);
      setLikeCount(previousCount);
      if (!token) {
        saveGuestLike(productId, previousLiked);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format count for display (e.g., 100, 1.2k)
  const formatCount = (count) => {
    if (!count || count < 100) return null;
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
    }
    return count.toString();
  };

  const formattedCount = formatCount(likeCount);

  return (
    <div
      className={`aminii-like-button-wrap ${className}`}
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`aminii-like-btn ${isLiked ? "liked" : ""}`}
        onClick={handleToggleLike}
        aria-label={isLiked ? "Unlike product" : "Like product"}
        title={isLiked ? "Unlike" : "Like"}
      >
        {isLiked ? (
          /* Red filled Heart */
          <svg
            className="aminii-like-icon liked"
            viewBox="0 0 24 24"
            fill="#e53935"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        ) : (
          /* Outline Heart matching user screenshot */
          <svg
            className="aminii-like-icon unliked"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        )}

        {/* Count is only visible once it reaches 100 */}
        {formattedCount && (
          <span className="aminii-like-count">{formattedCount}</span>
        )}
      </button>

      {showToast && (
        <div className="aminii-like-toast">Sign in to save likes</div>
      )}
    </div>
  );
}

export default LikeButton;
